import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitDropDown from '../../../../shared/UIKITComponents/DropDown';
import UIKitModal from '../../../../shared/UIKITComponents/Modal';
import { OptionValue } from '../../../../services/types/Misc';
import UIKitFileUploadButton from '../../../../shared/UIKITComponents/FileUploadButton';

interface UploadFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (documentTemplateId: number, formData: FormData) => void;
  templateOptions: OptionValue[];
}

interface FormValues {
  documentTypeId?: number;
  formData?: FormData;
}

const ALLOWED_FILE_EXTENSIONS = [
  '.pdf',
  '.txt',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.msg',
  '.jpg',
  '.jpeg',
  '.png',
  '.bmp',
];

const UploadFileModal = ({ isOpen, onClose, onSave, templateOptions }: UploadFileModalProps) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      documentTypeId: undefined,
      formData: undefined,
    },
    validationSchema: Yup.object({
      documentTypeId: Yup.number().required(t('required')).min(1, t('required')),
      formData: Yup.mixed().required(t('required')),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { resetForm }) => {
      if (values.documentTypeId && values.formData && !uploadError) {
        onSave(values.documentTypeId, values.formData);
        resetForm();
        setUploadError(null);
        onClose();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setUploadError(null);
    onClose();
  };

  const formDataHasError = !!uploadError || (formik.touched.formData && !!formik.errors.formData);
  const formDataErrorText =
    uploadError ||
    (formik.touched.formData && formik.errors.formData
      ? (formik.errors.formData as string)
      : undefined);

  return (
    <UIKitModal
      open={isOpen}
      onClose={handleClose}
      title={t('uploadFile')}
      size="sm"
      actions={
        <>
          <UIKitButton text={t('cancel')} variant="link" onClick={handleClose} color="secondary" />
          <UIKitButton
            text={t('save')}
            variant="flat"
            color="primary"
            disabled={!formik.isValid || !!uploadError || !formik.dirty}
            onClick={formik.submitForm}
          />
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <UIKitDropDown
              elements={templateOptions}
              value={formik.values.documentTypeId || null}
              onChange={(value) => formik.setFieldValue('documentTypeId', value)}
              label={t('typeOfDocument')}
              error={formik.touched.documentTypeId && !!formik.errors.documentTypeId}
              helperText={
                formik.touched.documentTypeId && formik.errors.documentTypeId
                  ? (formik.errors.documentTypeId as string)
                  : undefined
              }
              fullWidth
            />
          </Grid>
          <Grid size={12}>
            <UIKitFileUploadButton
              text={t('uploadFile')}
              variant="flat"
              allowedExtensions={ALLOWED_FILE_EXTENSIONS}
              onClick={(formData) => {
                setUploadError(formData ? null : t('required'));
                formik.setFieldTouched('formData', true);
                formik.setFieldValue('formData', formData);
              }}
              onError={(error) => {
                setUploadError(error);
                formik.setFieldTouched('formData', true);
                formik.setFieldValue('formData', undefined);
              }}
              error={formDataHasError}
              helperText={formDataErrorText}
            />
          </Grid>
        </Grid>
      </form>
    </UIKitModal>
  );
};

export default UploadFileModal;
